import type { ReactNode } from 'react';
import { initialsOf } from '../../lib/initials.js';
import './person-photo.css';
import './person-avatar.css';

function Stroke({ children }: { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/** Pessoa em traço: quem não tem foto nem nome que dê iniciais. */
export function PersonGlyph() {
  return (
    <Stroke>
      <circle cx="12" cy="8.6" r="3.6" />
      <path d="M5 19.4c.8-3.5 3.4-5.4 7-5.4s6.2 1.9 7 5.4" />
    </Stroke>
  );
}

/** Duas pessoas em traço: grupo sem foto. */
export function GroupGlyph() {
  return (
    <Stroke>
      <circle cx="9" cy="9" r="3" />
      <path d="M3.4 18.6c.6-3 2.7-4.6 5.6-4.6s5 1.6 5.6 4.6" />
      <circle cx="16.6" cy="9.6" r="2.4" />
      <path d="M16.4 14.2c2.2.2 3.7 1.6 4.2 4" />
    </Stroke>
  );
}

/**
 * Avatar de pessoa, um só para o app inteiro. Com foto, a foto cobre o
 * círculo; sem foto, as iniciais sobre um tom suave da cor da categoria (ou o
 * neutro, sem categoria). Grupo sem foto mostra duas pessoas em traço.
 *
 * Tamanho e formato vêm de `className` (a classe que cada lista já usa). A foto
 * entra por `photoUrl` ou, quando depende de um hook próprio, como filho
 * (`ContactPhoto`, `MxcPhoto`). Passe `label` só quando o nome não está escrito
 * ao lado; sem ele o avatar fica fora da leitura de tela.
 */
export function PersonAvatar({
  name,
  color,
  group = false,
  photoUrl,
  className,
  label,
  children,
}: {
  name: string | null | undefined;
  color?: string | null;
  group?: boolean;
  photoUrl?: string | null;
  className?: string;
  label?: string;
  children?: ReactNode;
}) {
  const initials = group ? '' : initialsOf(name);
  const background =
    color && !group ? `color-mix(in srgb, ${color} 18%, var(--surface))` : 'var(--s2)';
  const reading = label
    ? ({ role: 'img', 'aria-label': label } as const)
    : ({ 'aria-hidden': true } as const);
  return (
    <span className={className ? `pav ${className}` : 'pav'} style={{ background }} {...reading}>
      <span className="pav-in">{initials || (group ? <GroupGlyph /> : <PersonGlyph />)}</span>
      {photoUrl && <img className="person-photo" src={photoUrl} alt="" />}
      {children}
    </span>
  );
}
